import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Select, Store } from '@ngxs/store';
import { FetchTargetModules } from 'app/library/module/actions';
import { CourseOfferingExecutionsApiService } from 'app/library/course-offering-executions/services/course-offering-executions.api-service';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { Module } from 'app/library/module/models/module.model';
import { newModule } from 'app/library/module/models/newModule';
import { ModulesState } from 'app/library/module/models/modules.state';
import { ModuleService } from 'app/library/module/services/module.service';
import { FetchTags } from 'app/library/tag/actions';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { TagService } from 'app/library/tag/services/tag.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-allowed-modules-list',
    templateUrl: './allowed-modules-list.component.html',
    styleUrls: ['./allowed-modules-list.component.scss'],
})
export class AllowedModulesListComponent extends UnsubscribeOnDestroy implements OnInit, OnChanges, AfterViewInit {
    @Select(ModulesState.getTargetModules)
    private _filteredModule$: Observable<Module[]>;
    filteredModules: Module[];
    private _filteredModules = new BehaviorSubject<Module[]>([]);
    filteredModule$ = this._filteredModules.asObservable().pipe(
        filter((modules) => !!modules),
        map((modules) => {
            if (this._isModuleKindExecution()) {
                return modules;
            }

            const filteredByTags = modules.filter((tempModule) => {
                for (const selectedTag of this.tagMultiSelectService.selectedTags) {
                    if (tempModule.tags.map((tag) => tag.id).indexOf(selectedTag.id) === -1) {
                        return false;
                    }
                }

                return true;
            });

            const searchTerm = (this.searchInput.value || '').toString().toLowerCase().trim();

            if (!searchTerm) {
                return filteredByTags;
            }

            return filteredByTags.filter((tempModule) => {
                const name = tempModule.name || '';
                const version = tempModule.version || '';
                return name.toLowerCase().includes(searchTerm) || version.toLowerCase().includes(searchTerm);
            });
        })
    );

    tags: MaxBrainTag[];

    @Input()
    currentModule: Module;

    @Input()
    allowedKinds: ModuleKind[];

    @Output()
    choosenModule = new EventEmitter<Module>();

    @ViewChild(MatPaginator)
    paginator: MatPaginator;

    searchInput = new FormControl();
    isFetchingMoreModules = false;
    private _lastExecutionQueryKey = '';
    displayedColumns = ['avatar', 'name', 'version', 'buttons'];
    dataSource = new MatTableDataSource<Module>([]);
    totalCount = 0;
    pageSize = 5;
    selectedExecutions: Module[] = [];
    removable = true;

    constructor(
        private _store: Store,
        public tagService: TagService,
        public moduleService: ModuleService,
        public tagMultiSelectService: TagMultiSelectService,
        private _courseOfferingExecutionsApiService: CourseOfferingExecutionsApiService
    ) {
        super();

        this.tagMultiSelectService.setFromStorage('allowedModulesSelectedTags');
    }

    private _updateMyModules(resetPageIndex = false): void {
        this._filteredModules.next(this.filteredModules);
        if (resetPageIndex && this.paginator) {
            this.paginator.firstPage();
        }
    }

    private _isModuleKindExecution(): boolean {
        return this.currentModule?.kind === ModuleKind.Execution || this.currentModule?.kind === ModuleKind.ProgramExecution;
    }

    isExecutionMode(): boolean {
        return this._isModuleKindExecution();
    }

    getDatesOrVersion(module: Module): string {
        if (module?.startDate && module?.endDate) {
            return `${module.startDate.format('L')} - ${module.endDate.format('L')}`;
        }

        return module?.version || '-';
    }

    private _fetchExecutionTargets(resetPageIndex = false): void {
        if (resetPageIndex && this.paginator) {
            this.paginator.pageIndex = 0;
        }

        const tags = this.tagMultiSelectService.selectedTags.map((tag) => tag.id);
        const types = this.currentModule?.type ? [this.currentModule.type] : [];
        const pageSize = this.paginator?.pageSize || this.pageSize;
        const pageIndex = this.paginator?.pageIndex || 0;
        const queryKey = JSON.stringify({
            search: this.searchInput.value || '',
            tags,
            types,
            excludeId: this.currentModule?.id || null,
            pageIndex,
            pageSize,
        });

        if (queryKey === this._lastExecutionQueryKey) {
            return;
        }

        this._lastExecutionQueryKey = queryKey;

        this._courseOfferingExecutionsApiService
            .getAllExecutions(
                this.searchInput.value || '',
                pageSize,
                pageIndex + 1,
                '',
                '',
                ModuleStatuses.visibleKeys.filter((status) => status !== 'planned'),
                tags,
                types,
                [],
                null,
                [],
                'copy_to'
            )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((payload) => {
                const modules = payload.data.map((query) => newModule(query));
                this.filteredModules = this.currentModule ? modules.filter((module) => module.id !== this.currentModule.id) : modules;
                this.totalCount = payload.pagination.number_of_results;
                this.dataSource.data = this.filteredModules;
                this._updateMyModules();
            });
    }

    ngOnInit(): void {
        this.displayedColumns = this._isModuleKindExecution() ? ['avatar', 'name', 'version', 'content', 'buttons'] : ['avatar', 'name', 'version', 'buttons'];

        if (this.currentModule && !this._isModuleKindExecution()) {
            this._store.dispatch([new FetchTargetModules(this.currentModule), new FetchTags()]);
        } else if (this.currentModule && this._isModuleKindExecution()) {
            this._fetchExecutionTargets(true);
        }

        this.tagMultiSelectService.selectedTag$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((tags) => !!tags)
            )
            .subscribe((tags) => {
                if (this._isModuleKindExecution()) {
                    this._fetchExecutionTargets(true);
                } else {
                    this._updateMyModules(true);
                }
            });

        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe(() => {
            if (this._isModuleKindExecution()) {
                this._fetchExecutionTargets(true);
            } else {
                this._updateMyModules(true);
            }
        });

        this._filteredModule$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((modules) => !!modules)
            )
            .subscribe((modules) => {
                if (this._isModuleKindExecution()) {
                    return;
                }
                if (!this.currentModule) {
                    this.filteredModules = modules;
                    this._updateMyModules();
                    return;
                }
                const filteredModules = modules.filter((module) => module.id !== this.currentModule.id);
                this.filteredModules = this.allowedKinds?.length ? filteredModules.filter((module) => this.allowedKinds.includes(module.kind)) : filteredModules;
                this._updateMyModules();
            });

        this.filteredModule$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((modules) => !!modules)
            )
            .subscribe((modules) => {
                if (this._isModuleKindExecution()) {
                    return;
                }
                this.totalCount = modules.length;
                this.dataSource.data = modules;
                this._setPaginatorForMode();
            });
    }

    ngAfterViewInit(): void {
        this._setPaginatorForMode();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentModule?.currentValue) {
            this.displayedColumns = this._isModuleKindExecution() ? ['avatar', 'name', 'version', 'content', 'buttons'] : ['avatar', 'name', 'version', 'buttons'];
            if (this._isModuleKindExecution()) {
                this._setPaginatorForMode();
                this._fetchExecutionTargets(true);
            } else {
                this._setPaginatorForMode();
                this._store.dispatch([new FetchTargetModules(this.currentModule), new FetchTags()]);
            }
        }
    }

    onPageChange(event: PageEvent): void {
        if (!this._isModuleKindExecution()) {
            return;
        }

        this.pageSize = event.pageSize;
        this._fetchExecutionTargets(false);
    }

    isSelected(module: Module): boolean {
        return this.selectedExecutions.some((selected) => selected.id === module.id);
    }

    isSelectionLocked(module: Module): boolean {
        return this.selectedExecutions.length > 0 && !this.isSelected(module);
    }

    addToList(selectedModule: Module): void {
        this.selectedExecutions = [selectedModule];
        this.choosenModule.emit(selectedModule);
    }

    removeFromList(selectedModule?: Module): void {
        if (selectedModule) {
            this.selectedExecutions = this.selectedExecutions.filter((execution) => execution.id !== selectedModule.id);
        } else {
            this.selectedExecutions = [];
        }

        if (!this.selectedExecutions.length) {
            this.choosenModule.emit(null);
        }
    }

    onSelection(selectedModule: Module): void {
        setTimeout(() => {
            this.choosenModule.emit(selectedModule);
        });
    }

    private _setPaginatorForMode(): void {
        if (!this.paginator) {
            return;
        }

        if (this._isModuleKindExecution()) {
            this.dataSource.paginator = null;
        } else {
            this.dataSource.paginator = this.paginator;
        }
    }
}
